import type { ReactElement, RefObject } from 'react'
import type { TFunction } from 'i18next'
import { AlertCircle, CheckCircle, Download, FileText, Upload, X } from 'lucide-react'
import type { ScheduleImportResult } from '../../../types/collection-targets'

interface HeaderProps {
  onClose: () => void
  t: TFunction<'cases'>
}

export function Header({ onClose, t }: HeaderProps): ReactElement {
  return (
    <div className="items-centre mb-4 flex justify-between">
      <h3 className="font-heading text-lg font-semibold text-slate-800 dark:text-slate-100">
        {t('collections.scheduleImport.title')}
      </h3>
      <button
        onClick={onClose}
        className="rounded-lg p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
        aria-label={t('collections.scheduleImport.close')}
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

interface DownloadTemplateProps {
  downloading: boolean
  onDownloadTemplate: () => void
  t: TFunction<'cases'>
}

export function DownloadTemplate({
  downloading,
  onDownloadTemplate,
  t,
}: DownloadTemplateProps): ReactElement {
  return (
    <button
      onClick={onDownloadTemplate}
      disabled={downloading}
      className="items-centre mb-4 flex w-full gap-2 rounded-lg border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
    >
      <Download className="h-4 w-4 text-fuchsia-600 dark:text-fuchsia-400" />
      <div className="text-left">
        <div>{t('collections.scheduleImport.downloadTemplate')}</div>
        <div className="text-xs font-normal text-slate-400 dark:text-slate-500">
          {t('collections.scheduleImport.downloadTemplateDesc')}
        </div>
      </div>
    </button>
  )
}

interface ResultPanelProps {
  result: ScheduleImportResult
  onImportAnother: () => void
  t: TFunction<'cases'>
}

export function ResultPanel({ result, onImportAnother, t }: ResultPanelProps): ReactElement {
  return (
    <div className="mb-4 space-y-2 rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/50">
      <div className="items-centre flex gap-2">
        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
        <span className="font-heading text-sm font-semibold text-green-800 dark:text-green-400">
          {t('collections.scheduleImport.importResults')}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs text-green-700 dark:text-green-400">
        <span>{t('collections.scheduleImport.updatedCount', { count: result.updatedCount })}</span>
        <span>
          {t('collections.scheduleImport.unchangedCount', { count: result.unchangedCount })}
        </span>
        <span>
          {t('collections.scheduleImport.unmatchedCount', { count: result.unmatchedCount })}
        </span>
        {result.errorCount > 0 && (
          <span className="text-red-600 dark:text-red-400">
            {t('collections.scheduleImport.errorCount', { count: result.errorCount })}
          </span>
        )}
      </div>
      <button
        onClick={onImportAnother}
        className="mt-2 text-xs font-medium text-fuchsia-600 hover:text-fuchsia-700 dark:text-fuchsia-400 dark:hover:text-fuchsia-300"
      >
        {t('collections.scheduleImport.importAnother')}
      </button>
    </div>
  )
}

export function ErrorBanner({ error }: { error: string }): ReactElement {
  return (
    <div
      className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/50"
      role="alert"
    >
      <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-600 dark:text-red-400" />
      <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
    </div>
  )
}

interface DropZoneProps {
  importing: boolean
  dragOver: boolean
  setDragOver: (v: boolean) => void
  fileInputRef: RefObject<HTMLInputElement>
  onDrop: (event: React.DragEvent) => void
  onFileInput: (event: React.ChangeEvent<HTMLInputElement>) => void
  t: TFunction<'cases'>
}

export function DropZone(props: DropZoneProps): ReactElement {
  const { importing, dragOver, setDragOver, fileInputRef, onDrop, onFileInput, t } = props
  const containerClass = dragOver
    ? 'border-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-950/30'
    : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 hover:border-fuchsia-300 dark:hover:border-fuchsia-700'
  return (
    <div
      onDragOver={(event) => {
        event.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => {
        setDragOver(false)
      }}
      onDrop={onDrop}
      onClick={() => fileInputRef.current?.click()}
      className={`items-centre mb-4 flex cursor-pointer flex-col gap-3 rounded-xl border-2 border-dashed p-8 transition ${containerClass}`}
    >
      {importing ? <UploadingIndicator t={t} /> : <DropPrompt dragOver={dragOver} t={t} />}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={onFileInput}
        className="hidden"
      />
    </div>
  )
}

function UploadingIndicator({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div role="status" className="items-centre flex flex-col gap-2">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-fuchsia-600 border-t-transparent" />
      <span className="text-sm text-slate-600 dark:text-slate-400">
        {t('collections.scheduleImport.uploading')}
      </span>
    </div>
  )
}

function DropPrompt({ dragOver, t }: { dragOver: boolean; t: TFunction<'cases'> }): ReactElement {
  return (
    <>
      <div className="items-centre justify-centre flex h-10 w-10 rounded-full bg-fuchsia-100 dark:bg-fuchsia-950/50">
        {dragOver ? (
          <FileText className="h-5 w-5 text-fuchsia-600 dark:text-fuchsia-400" />
        ) : (
          <Upload className="h-5 w-5 text-fuchsia-600 dark:text-fuchsia-400" />
        )}
      </div>
      <span className="text-sm text-slate-500 dark:text-slate-400">
        {t('collections.scheduleImport.dragAndDrop')}
      </span>
    </>
  )
}

export function CloseFooter({
  onClose,
  t,
}: {
  onClose: () => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="flex justify-end">
      <button
        onClick={onClose}
        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('collections.scheduleImport.close')}
      </button>
    </div>
  )
}
